<template>
  <RootLayout resource="validator">
    <div
      class="container settings"
      :class="expandSettings ? 'expanded' : 'collapsed'"
    >
      <div class="inner">
        <div class="subheader">
          <div class="title">
            <font-awesome-icon
              :icon="['fas', 'cog']"
              @click="toggleExpandSettings()"
              class="icon-button"
            />
            Settings
          </div>
          <div class="buttons">
            <button
              class="btn btn-invisible-grey"
              @click="applyAnalyze()"
              :disabled="pendingConfigs.length !== 1"
            >
              analyse
            </button>
            <button class="btn btn-invisible-grey" @click="applySettings()">
              compare
            </button>
          </div>
        </div>
        <div class="settings-form">
          <h2>Tenant</h2>
          <div class="field select">
            <select class="tenant-selector" v-model="pendingTenant">
              <option v-for="t in tenants" :key="t.id" :value="t">
                {{ t.name }}
              </option>
            </select>
          </div>

          <h2>Output</h2>
          <div class="field select">
            <label for="reportLanguage"> Report language </label>
            <select v-model="reportLanguage" id="reportLanguage">
              <option
                v-for="language in supportedReportLanguages"
                :key="language"
                :value="language"
              >
                {{ languageLabels[language] }} ({{ language }})
              </option>
            </select>
          </div>

          <div class="field select">
            <label for="patientLanguage"> Patient language </label>
            <select v-model="patientLanguage" id="patientLanguage">
              <option
                v-for="language in supportedLanguages"
                :key="language"
                :value="language"
              >
                {{ languageLabels[language] }} ({{ language }})
              </option>
            </select>
          </div>

          <div class="field checkbox">
            <label for="showQuestionTable"> Show questions </label>
            <input
              type="checkbox"
              id="showQuestionTable"
              v-model="showQuestionTable"
            />
          </div>
          <div class="field checkbox">
            <label for="showRuleTable"> Show rules </label>
            <input type="checkbox" id="showRuleTable" v-model="showRuleTable" />
          </div>
          <div class="field checkbox" v-if="showRuleTable">
            <label for="hideExternalInputs"> Hide external inputs </label>
            <input
              type="checkbox"
              id="hideExternalInputs"
              v-model="hideExternalInputs"
            />
          </div>
          <div class="field checkbox">
            <label for="showReportTable"> Show report </label>
            <input
              type="checkbox"
              id="showReportTable"
              v-model="showReportTable"
            />
          </div>

          <h2>Configs</h2>
          <!-- Select which configurations to compare -->
          <ul class="selected-versions">
            <li v-for="(pair, i) in pendingConfigs" :key="pair.version.id">
              {{ pair.config.name }} - versie {{ pair.version.version }}
              <font-awesome-icon
                :icon="['fas', 'trash']"
                @click="removePendingConfig(i)"
                class="icon-button"
              />
            </li>
          </ul>
          <div class="field select">
            <select
              class="config-selector"
              v-model="pendingConfig"
              @change="pendingConfigChanged"
            >
              <option v-for="c in configs" :key="c.id" :value="c">
                {{ c.name }}
              </option>
            </select>
            <select
              class="version-selector"
              v-if="pendingVersion"
              v-model="pendingVersion"
            >
              <option
                v-for="v in pendingConfig.versions"
                :key="v.id"
                :value="v"
              >
                version {{ v.version }}
              </option>
            </select>
            <font-awesome-icon
              :icon="['fas', 'plus']"
              @click="addPendingConfig()"
              class="icon-button"
            />
          </div>
          <h2>Download</h2>
          <button
            class="action btn btn-dark"
            @click="downloadExcel"
            :disabled="isLoadingDownload"
          >
            <span>Download as Excel </span>
            <font-awesome-icon icon="spinner" v-if="isLoadingDownload" />
          </button>
        </div>
      </div>
    </div>
    <div
      class="evaluate"
      v-if="settings"
      :class="[
        hideExternalInputs ? 'hide-external-inputs' : '',
        expandSettings ? 'settings-expanded' : 'settings-collapsed',
      ]"
    >
      <div class="center" v-if="isLoading">
        <font-awesome-icon icon="spinner" size="2x" />
      </div>
      <table v-else>
        <thead v-if="table.sections.length && table.sections[0].header.length">
          <tr v-for="row in table.sections[0].header" :key="row.key">
            <th
              v-for="cell in row.cells"
              :key="cell.key"
              :colspan="cell.colspan"
            >
              {{ cell.display }}
            </th>
          </tr>
        </thead>
        <template v-for="(section, i) in table.sections">
          <!--The first header is already displayed in thead (of which only one is
              allowed). Include a second header as a tbody. -->
          <tbody
            v-if="i !== 0"
            :key="`${section.key}-header`"
            class="intermediate-header"
          >
            <tr v-for="row in section.header" :key="row.key">
              <th
                v-for="cell in row.cells"
                :key="cell.key"
                :colspan="cell.colspan"
              >
                {{ cell.display }}
              </th>
            </tr>
          </tbody>
          <tbody :key="section.key">
            <tr
              v-for="row in section.body"
              :key="row.key"
              :class="[
                isExternalInput(row) ? 'external-input' : '',
                focusClass(row),
              ]"
            >
              <template v-for="cell in row.cells">
                <th
                  v-if="cell.type === 'header'"
                  :key="cell.key"
                  :colspan="cell.colspan"
                  :title="cell.title"
                  :class="{
                    'row-label': hasAttribute(cell, 'label'),
                    'row-diff': hasAttribute(cell, 'diffs'),
                    'has-diff': hasAttribute(cell, 'has-diff'),
                    'no-diff': hasAttribute(cell, 'no-diff'),
                    'tag-container': hasTags(cell),
                  }"
                  @click="toggleFocus(row)"
                >
                  <!-- Output level indicator -->
                  <span
                    v-if="outputLevel(cell)"
                    :class="['output-level', outputLevel(cell)]"
                  ></span>

                  <!-- Tag list if the cell has tags, shortened title otherwise. -->
                  <span class="tags" v-if="hasTags(cell)">
                    <span
                      class="tag"
                      v-for="tag in tags(cell)"
                      :key="tag.tag"
                      :class="tag.type"
                      >{{ tag.tag }}</span
                    >
                  </span>
                  <template v-else
                    >{{
                      cell.colspan > 2
                        ? cell.display
                        : shorten(cell.display, 50)
                    }}
                  </template>
                </th>
                <td
                  v-else
                  :key="cell.key"
                  :class="[
                    cellClass(cell),
                    cell.value_missing ? 'missing' : null,
                  ]"
                  :title="cell.title"
                >
                  {{ shorten(cell.display) }}
                </td>
              </template>
            </tr>
          </tbody>
        </template>
      </table>
    </div>
    <config-analyzer
      v-if="!!analyzeConfig"
      :settings="analyzeConfig"
    ></config-analyzer>
  </RootLayout>
</template>
<script setup>
import RootLayout from '@/components/RootLayout.vue';
import { computed, onMounted, ref } from 'vue';
import { downloadTable, evaluateToTable, getConfigs, getTenants } from '@/api';
import { cloneDeep, last, truncate } from 'lodash';
import ConfigAnalyzer from '@/components/config-validator/ConfigAnalyzer.vue';
import {
  languageLabels,
  supportedLanguages,
  supportedReportLanguages,
} from '@/languages';

// Toggle settings width for more space for the table
const expandSettings = ref(true);

function toggleExpandSettings() {
  expandSettings.value = !expandSettings.value;
}

const showQuestionTable = ref(true);
const patientLanguage = ref('nl');

const showRuleTable = ref(true);
const showReportTable = ref(true);
const reportLanguage = ref('nl');

// Show / hide rules that just pass an external input value
const hideExternalInputs = ref(false);

// Available tenants
const tenants = ref([]);

// Available configurations
const configs = ref([]);

// ID of the tenant in the tenant selector
const pendingTenant = ref(null);

// Versions that will be evaluated with the next submit
const pendingConfigs = ref([]);

// Config to pass along to the analyzer
const analyzeConfig = ref(null);

// Currently selected config / version
const pendingConfig = ref(null);
const pendingVersion = ref(null);

// Callback when the pending config value is changed.
function pendingConfigChanged() {
  if (!pendingConfig.value) {
    return;
  }

  pendingVersion.value = pendingConfig.value.versions[0];
}

// Adds the current pending config to the list of pending configs
function addPendingConfig() {
  if (!pendingConfig.value || !pendingVersion.value) {
    return;
  }

  if (
    pendingConfigs.value.find((c) => c.version.id === pendingVersion.value.id)
  ) {
    // Don't add duplicates
    return;
  }

  pendingConfigs.value.push({
    config: pendingConfig.value,
    version: pendingVersion.value,
  });
}

// Removes the pending config at the given index
function removePendingConfig(i) {
  pendingConfigs.value.splice(i, 1);
}

// Current (pending) config settings
const settings = ref(null);

const table = ref(null);

// Clears settings, sections, rule dependencies,
// headers and rule cells.
function resetData() {
  table.value = null;
}

const isLoading = computed(() => !table.value);

async function applyAnalyze() {
  const counter = analyzeConfig.value?.counter ?? 0;
  resetData();

  // Set a config with a counter, so a new button press would refresh the page.
  analyzeConfig.value = {
    data: pendingConfigs.value[0],
    counter: counter + 1,
  };
}

// Initializes config comparison
async function applySettings() {
  resetData();

  // Collapse settings when they are applied
  expandSettings.value = false;
  const toEvaluate = pendingConfigs.value;
  const tenantId = pendingTenant.value.id;

  // Update current settings
  settings.value = {
    configs: cloneDeep(toEvaluate),
    tenantId: tenantId,
  };

  // Fetch all evaluations
  const versionIds = toEvaluate.map((config) => config.version.id);
  table.value = (
    await evaluateToTable(
      tenantId,
      reportLanguage.value,
      patientLanguage.value,
      versionIds,
      showQuestionTable.value,
      showReportTable.value,
      showRuleTable.value
    )
  ).data;
}

const isLoadingDownload = ref(false);

async function downloadExcel() {
  isLoadingDownload.value = true;
  try {
    const toEvaluate = pendingConfigs.value;
    const tenantId = pendingTenant.value.id;
    const versionIds = toEvaluate.map((config) => config.version.id);

    await downloadTable(
      tenantId,
      reportLanguage.value,
      patientLanguage.value,
      versionIds,
      showQuestionTable.value,
      showReportTable.value,
      showRuleTable.value
    );
  } finally {
    isLoadingDownload.value = false;
  }
}

// Loads all validation tenants
async function loadTenants() {
  const allTenants = (await getTenants()).data.data;
  tenants.value = allTenants.filter((t) => t.status === 'validation');
}

// Loads current configurations
async function loadConfigs() {
  configs.value = (await getConfigs()).data;

  // Preselect E+POS - vragenlijst
  pendingConfig.value =
    configs.value.find((c) => c.name.includes('- vragenlijst')) ??
    configs.value[0];
  pendingVersion.value = pendingConfig.value.versions[0];
}

//#region Display helpers
function shorten(value, length = 10) {
  value = value?.toString();
  return truncate(value, { length });
}

function hasAttribute(rowOrCell, attr) {
  return rowOrCell.attributes.includes(attr);
}

function isExternalInput(row) {
  return hasAttribute(row, 'external-input');
}

function cellClass(cell) {
  if (!cell.category) {
    // Default white / transparent
    return 'cat-0';
  }

  // Divide the rest over the available 10 colors, not repeating white.
  const cat = ((cell.category - 1) % 10) + 1;
  return `cat-${cat}`;
}

function outputLevel(cell) {
  for (const attr of cell.attributes) {
    if (attr.startsWith('output-level')) {
      return attr;
    }
  }

  return null;
}

function hasTags(cell) {
  return hasAttribute(cell, 'tags');
}

function tags(cell) {
  if (!cell.display) {
    return [];
  }

  return cell.display.split(' ').map((t) => {
    const parts = t.split(':');
    return {
      type: parts[0],
      tag: parts[1],
    };
  });
}

const focusStack = ref([]);
const focused = computed(() =>
  focusStack.value.length ? last(focusStack.value) : null
);
const focusedRefs = computed(() => {
  if (!focused.value?.reference || !table.value) {
    return new Set();
  }

  const deps = table.value.dependencies[focused.value.reference] ?? [];
  return new Set(deps);
});

function toggleFocus(row) {
  if (focused.value?.reference === row.reference) {
    focusStack.value.pop();
  } else {
    focusStack.value.push(row);
  }
}

// Class applied to a rule row. If a rule is focused, this is one of:
// - 'focused' (current rule is focused)
// - 'dependency' (rule is a dependency of the focused rule)
// - 'hidden' (rule is not a dependency of the focused rule)
// If no rule is focused, this returns null
function focusClass(row) {
  const current = focused.value;
  if (!current) {
    return null;
  }

  // So we can highlight the focused rule
  if (current.reference === row.reference) {
    return 'focused';
  }

  // focusedRefs as a computed prop is helpful, so we have a
  // single "static" set if the focused row updates. Should
  // be a minor performance gain over lots of rows.
  return focusedRefs.value.has(row.reference) ? 'dependency' : 'hidden';
}

//#endregion

onMounted(async () => {
  await Promise.all([loadConfigs(), loadTenants()]);

  pendingTenant.value = tenants.value.length ? tenants.value[0] : null;
  pendingConfigs.value = [
    {
      config: pendingConfig.value,
      version: pendingVersion.value,
    },
  ];
});
</script>

<style scoped lang="scss">
@import '@/assets/mixins.scss';

$settings-collapsed: 50px;
$settings-expanded: 380px;

.settings {
  width: $settings-expanded;
  flex-basis: $settings-expanded;
  flex-grow: 0;
  flex-shrink: 0;
  border-right: solid 1px $bg-color-border;

  &.collapsed {
    width: $settings-collapsed;
    flex-basis: $settings-collapsed;
    overflow: hidden;
  }

  .inner {
    width: $settings-expanded;
    overflow: hidden;
  }
}

.settings-form {
  padding: 0.5rem;
}

.checkbox {
  line-height: 24px;
}

.version-selector {
  margin-left: 0.5rem;
}

.icon-button {
  margin: 0 0.5rem;
  cursor: pointer;
}

.config-selector {
  max-width: 200px;
}

// deep so the ConfigAnalyzer can use the same side panel styles.
:deep(.evaluate) {
  min-width: calc(100% - 90px - $settings-expanded);
  padding: 1rem;
  box-sizing: border-box;
  overflow-x: auto;

  &.settings-collapsed {
    min-width: calc(100% - 90px - $settings-collapsed);
  }
}

h2 {
  font-weight: bold;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.selected-versions {
  margin: 0 0 1rem 0;
  padding: 0;
  list-style: none;

  li {
    margin-left: 0;
    font-size: 0.9rem;
    line-height: 24px;
  }
}

table,
th,
td {
  border: 1px solid $bg-color-border;
  border-collapse: collapse;
  text-align: center;
}

tbody th {
  text-align: left;

  &.tag-container {
    text-align: center;
  }
}

td.last {
  border-right: 1px solid #666666;
}

table {
  font-size: 0.8rem;
}

th,
td {
  padding: 4px;

  // Always stay on one line, if necessary we apply a
  // different mechanism to keep things in check.
  white-space: nowrap;
}

th {
  background: #f0f0f0;
}

thead,
.intermediate-header {
  th {
    text-align: center;
    height: 1.2rem;
    line-height: 1.2rem;
    position: sticky;

    // Container padding is 1rem, that's where the
    // first line stops. The second line stops just
    // below that.
    top: calc(0.2rem + 1px);
    white-space: nowrap;
    background: #979494;
  }

  tr:first-of-type th {
    top: -1rem;
  }
}

.row-diff {
  font-weight: normal;
}

.has-diff {
  background: #8bffc0;
}

.no-diff {
  background: #f5c6cb;
}

.row-label {
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.focused .row-label {
  color: darkblue;
  text-decoration: underline;
}

// Rules that aren't part of our focus scope
.hidden {
  display: none;
}

.hide-external-inputs .external-input {
  display: none;
}

.field + .field {
  margin-top: 1rem;
}

select {
  @include input-box;
}

// Cell backgrounds
.cat-0 {
  background-color: #fff;
}

.cat-1 {
  background-color: #add8e6;
}

.cat-2 {
  background-color: #98fb98;
}

.cat-3 {
  background-color: #ffffcc;
}

.cat-4 {
  background-color: #e6e6fa;
}

.cat-5 {
  background-color: #ffdab9;
}

.cat-6 {
  background-color: #f5fffa;
}

.cat-7 {
  background-color: #ffb6c1;
}

.cat-8 {
  background-color: #87ceeb;
}

.cat-9 {
  background-color: #fffdd0;
}

.cat-10 {
  background-color: #c8a2c8;
}

.missing {
  font-style: italic;
  color: #8e8e8e;
  text-align: center;
}

.output-level {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: white;
  margin-right: 3px;
}

.output-level-problem,
.output-level-warning {
  background-color: #ff8000;
}

.output-level-critical,
.output-level-danger {
  background-color: red;
}

.tags {
  display: inline-flex;
  flex-wrap: nowrap;
}
</style>
